import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  TextField,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import { CustomTextField, TypographySmall } from "../../customComponent";
import { editViewer } from "../../../store/actions/viewerAction";
import { colorStyling } from "../../../helpers/color";
import DispatchErrorPromptAlert from "../../DispatchErrorPromptAlert";
import { SET_ERROR_PROMPT_VIEWER_DIALOG } from "../../../store/actionTypes/viewerActionTypes";
import { parseVariables } from "../../../helpers/localize";
import { sanitizeInput } from "../../../helpers/inputProcessing";
import { isEmpty } from "lodash";
import { stellarFlag } from "../../../externalFeatures/componentsFlag";

const EditViewer = ({ viewer }) => {
  const dispatch = useDispatch();
  const {
    isErrorPromptViewerDialog: isError,
    errorMessageViewerDialog: errorMessage,
  } = useSelector((state) => state.viewer);

  const { t } = useTranslation("viewer");
  const { t: tCommon } = useTranslation();

  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [viewerLocation, setViewerLocation] = useState("");
  const [viewerLocationDesc, setViewerLocationDesc] = useState("");

  const [validation, setValidation] = useState("");
  const [loading, setLoading] = useState(false);
  const [coordinate, setCoordinate] = useState(false);
  const [latitude, setLatitude] = useState(0);
  const [longitude, setLongitude] = useState(0);

  const [vistarVenueID, setVistarVenueID] = useState("");
  const [vistarDeviceID, setVistarDeviceID] = useState("");
  const [hivestackID, setHivestackID] = useState("");
  const maxLocationLength = 60;

  const handleClickClose = () => {
    setOpen(false);
    setName("");
    setValidation("");
    setCoordinate(false);
    setLatitude(0);
    setLongitude(0);
    setVistarVenueID("");
    setVistarDeviceID("");
    setHivestackID("");
    dispatch({ type: SET_ERROR_PROMPT_VIEWER_DIALOG, payload: false });
  };

  const handleInputName = (input) => {
    if (input.length > 40)
      setValidation(
        parseVariables(tCommon("validation.maxLengthChars"), {
          num: 40,
        })
      );
    else {
      setName(input);
      if (validation) setValidation("");
    }
  };

  const handleInputLocation = (input) => {
    if (input.length <= maxLocationLength) setViewerLocation(input);
  };

  const handleInputLocationDesc = (input) => {
    if (input.length <= maxLocationLength) setViewerLocationDesc(input);
  };
  const handleCoordinateToggle = (e) => {
    setCoordinate(e.target.checked);
  };

  const handleCoordinateChange = (line, e) => {
    let val = e.target.value;

    // Remove leading zeros but keep "0." or "-0."
    val = val.replace(/^(-?)0+(\d)/, "$1$2");

    // Allow empty input but reset to "0" if cleared
    if (val === "" || val === "-") {
      val = "0";
    }

    // Ensure valid number input, allowing a trailing "."
    if (/^-?\d*\.?\d*$/.test(val)) {
      const num = parseFloat(val);

      if (line === "latitude" && num >= -90 && num <= 90) {
        setLatitude(val);
      } else if (line === "longitude" && num >= -180 && num <= 180) {
        setLongitude(val);
      }
    }
  };

  const handleFinishSubmit = (stateSubmit) => {
    setLoading(false);
    if (stateSubmit === "success") {
      handleClickClose();
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    let payload = {};
    payload["metadata"] = {
      address1: viewer?.location || null,
      address2: viewer?.locationDesc || null,
      vistarDeviceId: viewer?.externalInfo?.vistarDeviceId || null,
      vistarVenueId: viewer?.externalInfo?.vistarVenueId || null,
      hivestackScreenId: viewer?.externalInfo?.hivestackScreenId || null,
    };

    if (name.trim() === "") {
      setValidation(t("validation.nameEmpty"));
      return;
    }

    if (name !== viewer.name) {
      payload["name"] = name;
    }

    if (viewerLocation !== viewer.location) {
      payload["metadata"]["address1"] =
        viewerLocation === "" ? null : sanitizeInput(viewerLocation.trim());
    }

    if (viewerLocationDesc !== viewer.locationDesc) {
      payload["metadata"]["address2"] =
        viewerLocationDesc === ""
          ? null
          : sanitizeInput(viewerLocationDesc.trim());
    }

    if (coordinate) {
      if (latitude !== viewer?.coordinate?.latitude)
        payload["latitude"] = +latitude;
      if (longitude !== viewer?.coordinate?.longitude)
        payload["longitude"] = +longitude;
    } else {
      payload["latitude"] = null;
      payload["longitude"] = null;
    }

    if (vistarDeviceID !== viewer?.externalInfo?.vistarDeviceId) {
      payload["metadata"]["vistarDeviceId"] =
        vistarDeviceID === "" ? null : sanitizeInput(vistarDeviceID.trim());
    }

    if (vistarVenueID !== viewer?.externalInfo?.vistarVenueId) {
      payload["metadata"]["vistarVenueId"] =
        vistarVenueID === "" ? null : sanitizeInput(vistarVenueID.trim());
    }

    if (hivestackID !== viewer?.externalInfo?.hivestackScreenId) {
      payload["metadata"]["hivestackScreenId"] =
        hivestackID === "" ? null : sanitizeInput(hivestackID.trim());
    }

    if (!isEmpty(payload)) {
      payload = { ...payload, id: viewer.id, viewer };

      setLoading(true);
      if (payload.name) payload["name"] = sanitizeInput(payload.name);

      dispatch(editViewer(payload, handleFinishSubmit));
    } else {
      handleClickClose();
    }
  };

  useEffect(() => {
    if (open) {
      setName(viewer?.name);
      setViewerLocation(viewer?.location ?? "");
      setViewerLocationDesc(viewer?.locationDesc ?? "");
      setVistarVenueID(viewer?.externalInfo?.vistarVenueId ?? "");
      setVistarDeviceID(viewer?.externalInfo?.vistarDeviceId ?? "");
      setHivestackID(viewer?.externalInfo?.hivestackScreenId ?? "");

      if (viewer?.coordinate) {
        const viewerCoordinate = viewer?.coordinate;
        if (viewerCoordinate?.latitude && viewerCoordinate?.longitude) {
          setLatitude(viewerCoordinate?.latitude ?? 0);
          setLongitude(viewerCoordinate?.longitude ?? 0);
          setCoordinate(true);
        }
      } else {
        setLatitude(0);
        setLongitude(0);
        setCoordinate(false);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <>
      <Button
        startIcon={<EditIcon fontSize="small" />}
        variant="contained"
        sx={{ boxShadow: 3, ml: 2 }}
        onClick={() => setOpen(true)}
      >
        {t("item.editBtn")}
      </Button>

      <Dialog
        maxWidth="sm"
        fullWidth
        open={open}
        keepMounted
        onClose={handleClickClose}
      >
        <Box component="form" onSubmit={handleSubmit}>
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {t("form.editDialogTitle")}
            <IconButton onClick={handleClickClose}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <Divider />

          <DialogContent>
            <DispatchErrorPromptAlert
              isError={isError}
              errorMessage={errorMessage}
              closeActionType={SET_ERROR_PROMPT_VIEWER_DIALOG}
            />

            <Box sx={{ mt: 1 }}>
              <CustomTextField
                fullWidth
                size="small"
                label={t("form.nameLabel")}
                margin="dense"
                name="name"
                value={name}
                onChange={(e) => handleInputName(e.target.value)}
                placeholder={t("form.namePlaceholder")}
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                disabled={loading}
              />
              <TypographySmall sx={{ color: colorStyling.red }}>
                {validation}
              </TypographySmall>
            </Box>

            <Box sx={{ mt: 1 }}>
              <CustomTextField
                fullWidth
                size="small"
                label={t("form.locationLabel")}
                margin="dense"
                name="location"
                value={viewerLocation}
                onChange={(e) => handleInputLocation(e.target.value)}
                placeholder={t("form.locationPlaceholder")}
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                disabled={loading}
              />
              {viewerLocation.length >= maxLocationLength && (
                <TypographySmall sx={{ color: colorStyling.red }}>
                  {parseVariables(tCommon("validation.maxLengthChars"), {
                    num: maxLocationLength,
                  })}
                </TypographySmall>
              )}
            </Box>

            {viewerLocation && (
              <Box sx={{ mt: 1 }}>
                <CustomTextField
                  fullWidth
                  size="small"
                  label={t("form.locationDescLabel")}
                  margin="dense"
                  name="locationDesc"
                  value={viewerLocationDesc}
                  onChange={(e) => handleInputLocationDesc(e.target.value)}
                  placeholder={t("form.locationDescPlaceholder")}
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  disabled={loading}
                />
                {viewerLocationDesc.length >= maxLocationLength && (
                  <TypographySmall sx={{ color: colorStyling.red }}>
                    {parseVariables(tCommon("validation.maxLengthChars"), {
                      num: maxLocationLength,
                    })}
                  </TypographySmall>
                )}
              </Box>
            )}

            <Box sx={{ mt: 1 }}>
              <FormControlLabel
                control={<Checkbox />}
                checked={coordinate}
                onChange={handleCoordinateToggle}
                label={t("form.insertCoordinateLabel")}
                disabled={loading}
                sx={{ mb: 1 }}
              />
              {coordinate && (
                <Box sx={{ display: "flex", alignItems: "end", mb: 1 }}>
                  <Box sx={{ width: "100%" }}>
                    <TextField
                      fullWidth
                      label={t("form.latitudeLabel")}
                      placeholder={t("form.coordinatePlaceholder")}
                      type="text"
                      value={latitude}
                      onChange={(e) => handleCoordinateChange("latitude", e)}
                      size="small"
                      disabled={loading}
                    />
                  </Box>
                  <Box sx={{ width: "100%", ml: 2 }}>
                    <TextField
                      fullWidth
                      label={t("form.longitudeLabel")}
                      placeholder={t("form.coordinatePlaceholder")}
                      type="text"
                      value={longitude}
                      onChange={(e) => handleCoordinateChange("longitude", e)}
                      size="small"
                      disabled={loading}
                    />
                  </Box>
                </Box>
              )}
            </Box>

            {stellarFlag && (
              <>
                <Box sx={{ mt: 1 }}>
                  <Box>
                    <CustomTextField
                      label={t("form.hivestackScreenIDLabel")}
                      size="small"
                      autoFocus
                      margin="dense"
                      fullWidth
                      name="hivestackScreenID"
                      placeholder={t("form.hivestackScreenIDPlaceholder")}
                      value={hivestackID}
                      onChange={(e) => {
                        setHivestackID(e.target.value);
                      }}
                      disabled={loading}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Box>
                </Box>
                <Box
                  width={"100%"}
                  display={"flex"}
                  justifyContent={"space-between"}
                  sx={{ mt: 1 }}
                >
                  <Box sx={{ width: "100%" }}>
                    <CustomTextField
                      label={t("form.vistarDeviceIDLabel")}
                      size="small"
                      autoFocus
                      margin="dense"
                      fullWidth
                      name="vistarDeviceID"
                      placeholder={t("form.vistarDeviceIDPlaceholder")}
                      value={vistarDeviceID}
                      onChange={(e) => {
                        setVistarDeviceID(e.target.value);
                      }}
                      disabled={loading}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Box>

                  <Box sx={{ width: "100%", ml: 2 }}>
                    <CustomTextField
                      label={t("form.vistarVenueIDLabel")}
                      size="small"
                      autoFocus
                      margin="dense"
                      fullWidth
                      name="vistarVenueID"
                      placeholder={t("form.vistarVenueIDPlaceholder")}
                      value={vistarVenueID}
                      onChange={(e) => {
                        setVistarVenueID(e.target.value);
                      }}
                      disabled={loading}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Box>
                </Box>
              </>
            )}
          </DialogContent>

          <DialogActions>
            <Button fullWidth variant="outlined" onClick={handleClickClose}>
              {tCommon("cancelBtn")}
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : tCommon("saveBtn")}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </>
  );
};

export default EditViewer;
